import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { ConstructionProgressView } from '@euler/app/tv/components/detail/ConstructionProgressView';
import { InspectionProgressView } from '@euler/app/tv/components/detail/InspectionProgressView';
import { PreInspectionProgressView } from '@euler/app/tv/components/detail/PreInspectionProgressView';
import { VehicleInfoBannerView } from '@euler/app/tv/components/detail/VehicleInfoBannerView';
import { TaskScreenContent } from '@euler/app/tv/components/TaskScreenContent';
import {
  kTaskDetailHeaderHeight,
  kVehicleBannerHeight,
} from '@euler/app/tv/functions/constants';
import { RecentVisitedTaskManager } from '@euler/app/tv/functions/RecentVisitedTaskManager';
import {
  taskDetailToBookmark,
  useScreenContentPadding,
} from '@euler/app/tv/functions/utils';
import type { TaskNavParams } from '@euler/app/tv/pages/TaskFlow';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef } from 'react';
import { Platform, ScrollView } from 'react-native';

export const TaskDetailScreen = wrapNavigatorScreen(() => {
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const { detail, taskManager } = useTaskContext();
  const extraInfo = useTaskExtraInfo();
  const { rpx } = useResponsiveContext();

  // calculate the maximum cell height
  const [paddingLeft, paddingRight, insets] = useScreenContentPadding();
  const { width, height } = useContainerLayout();
  const headerHeight = rpx(kTaskDetailHeaderHeight) + insets.top;
  const bannerHeight = rpx(kVehicleBannerHeight);
  const maxCellHeight =
    height - headerHeight - bannerHeight - insets.bottom - rpx(40) * 2;

  const count = (detail.inspection.flows.length || 1) + 2;
  const [cellWidth, expectedCellHeight] = computeGridLayoutItemSize({
    numberOfColumns: count === 3 ? 3 : 3.5,
    gridWidth: width - paddingLeft - paddingRight + rpx(40),
    cellMargin: { left: 0, top: 0, bottom: 0, right: rpx(40) },
    aspectRatio: 400 / 500,
    gridPadding: 0,
  });
  const cellHeight = Math.min(maxCellHeight, expectedCellHeight);

  useEffect(() => {
    return navigation.addListener('beforeRemove', () => {
      taskManager.end();
    });
  }, [navigation, taskManager]);

  const bookmark = useRef(taskDetailToBookmark(detail)).current;

  useEffect(() => {
    RecentVisitedTaskManager.shared.add(bookmark);
  }, [bookmark]);

  const onPreInspectionPress = useCallback(() => {
    navigation.navigate('PreInspection', {});
  }, [navigation]);

  const onInspectionPress = useCallback(
    (flowId?: string) => {
      navigation.navigate('Inspection', { flowId });
    },
    [navigation],
  );

  const onConstructionPress = useCallback(() => {
    navigation.navigate('Construction', {});
  }, [navigation]);

  return (
    <TaskScreenContent>
      <VehicleInfoBannerView
        detail={detail}
        extraInfo={extraInfo}
        css={`
          margin-bottom: ${rpx(40)}px;
          margin-left: ${paddingLeft}px;
          margin-right: ${paddingRight}px;
        `}
      />

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        contentContainerStyle={{
          paddingLeft,
          paddingRight,
          alignItems: 'center',
        }}
        css={`
          flex: 1;
        `}
      >
        <PreInspectionProgressView
          detail={detail}
          onPress={onPreInspectionPress}
          css={`
            width: ${cellWidth}px;
            height: ${cellHeight}px;
          `}
        />
        <InspectionProgressView
          detail={detail}
          onPress={onInspectionPress}
          css={`
            width: ${cellWidth}px;
            height: ${cellHeight}px;
          `}
        />
        <ConstructionProgressView
          detail={detail}
          onPress={onConstructionPress}
          css={`
            width: ${cellWidth}px;
            height: ${cellHeight}px;
          `}
        />
      </ScrollView>
    </TaskScreenContent>
  );
});
